import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { CombinedWorkPane } from '../components/crono/CombinedWorkPane';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import {
  addChronoElement,
  addChronoObservations,
  addChronoResource,
  listChronoProcesses,
  listChronoProjects,
  loadChronoBundle,
  removeChronoElement,
  removeChronoObservation,
  removeChronoResource,
  updateChronoElement,
  updateChronoStudy,
  type ChronoBundle,
  type ResourceKind,
} from '../services/chronoService';
import {
  formatChronoNumber,
  observationStats,
  quickAnalysis,
  representativeSeconds,
  taktSeconds,
  type ElementNature,
  type RepresentativeMethod,
  type ValueClass,
} from '../utils/chronoStats';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Em cronometragem',
  approved: 'Aprovado',
  archived: 'Arquivado',
};
const METHOD_LABEL: Record<RepresentativeMethod, string> = {
  mean: 'Média',
  median: 'Mediana',
  minimum: 'Mínimo',
  maximum: 'Máximo',
  manual: 'Manual',
};
const NATURE_LABEL: Record<ElementNature, string> = {
  manual: 'Manual',
  machine: 'Máquina',
  walking: 'Caminhada',
  waiting: 'Espera',
};
const fieldClass = 'mt-0.5 w-full border border-slate-300 bg-white px-1.5 py-1 text-xs dark:border-slate-600 dark:bg-slate-950';

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Não foi possível gravar.';
}

export const CronoStudyPage: React.FC = () => {
  const { id = '' } = useParams();
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();
  const [tab, setTab] = useState<'estudo' | 'cronometragem' | 'combinado' | 'analise'>('estudo');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const bundleQuery = useQuery({
    queryKey: ['chrono-study', organizationId, id],
    queryFn: () => loadChronoBundle(organizationId, id),
    enabled: Boolean(organizationId && id),
  });
  const projectsQuery = useQuery({
    queryKey: ['chrono-projects', organizationId],
    queryFn: () => listChronoProjects(organizationId),
    enabled: Boolean(organizationId),
  });
  const processesQuery = useQuery({
    queryKey: ['chrono-processes', organizationId],
    queryFn: () => listChronoProcesses(organizationId),
    enabled: Boolean(organizationId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['chrono-study', organizationId, id] });
    await queryClient.invalidateQueries({ queryKey: ['chrono-studies', organizationId] });
  };
  const fail = (error: unknown) => showToast('error', message(error));
  const bundle = bundleQuery.data;

  useEffect(() => {
    if (bundle && !selectedId && bundle.elements[0]) setSelectedId(bundle.elements[0].id);
  }, [bundle, selectedId]);

  return (
    <ModuleLayout>
      <div className="flex min-h-full flex-col gap-2 p-2 text-slate-800 dark:text-slate-100">
        <button type="button" onClick={() => navigate('/cronoanalise')} className="w-fit text-[11px] text-blue-700">← Estudos</button>
        {bundleQuery.isLoading && <p className="text-xs">Carregando estudo…</p>}
        {bundleQuery.isError && <p className="text-xs text-red-700">{message(bundleQuery.error)}</p>}
        {bundle === null && !bundleQuery.isLoading && <p className="text-xs">Estudo não encontrado nesta organização.</p>}
        {bundle && (
          <>
            <StudyHeader bundle={bundle} />
            <div className="flex gap-1 text-xs">
              {(['estudo', 'cronometragem', 'combinado', 'analise'] as const).map((item) => (
                <button key={item} type="button" onClick={() => setTab(item)} className={`border px-2 py-1 ${tab === item ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900' : 'border-slate-300'}`}>
                  {item === 'estudo' ? 'Estudo' : item === 'cronometragem' ? 'Cronometragem' : item === 'combinado' ? 'Trabalho Combinado' : 'Análise'}
                </button>
              ))}
            </div>
            {tab === 'estudo' && (
              <StudyPane
                bundle={bundle}
                projects={projectsQuery.data ?? []}
                processes={processesQuery.data ?? []}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onSaved={refresh}
                onError={fail}
              />
            )}
            {tab === 'cronometragem' && (
              <TimingPane bundle={bundle} selectedId={selectedId} onSelect={setSelectedId} onSaved={refresh} onError={fail} />
            )}
            {tab === 'combinado' && <CombinedWorkPane bundle={bundle} onSaved={refresh} onError={fail} />}
            {tab === 'analise' && <AnalysisPane bundle={bundle} />}
          </>
        )}
      </div>
      <ToastContainer />
    </ModuleLayout>
  );
};

function StudyHeader({ bundle }: { bundle: ChronoBundle }) {
  const takt = taktSeconds(bundle.study.availableSeconds, bundle.study.demandQuantity);
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900">
      <h1 className="text-sm font-semibold">{bundle.study.title}</h1>
      <span className="text-[11px]">{STATUS_LABEL[bundle.study.status]}</span>
      <span className="text-[11px]">{bundle.study.operationLabel}</span>
      <span className="text-[11px] text-slate-500">{bundle.study.productFamily || 'Sem produto/família'}</span>
      <strong className="ml-auto text-sm tabular-nums">Takt {formatChronoNumber(takt, 2)} s</strong>
    </div>
  );
}

function StudyPane({
  bundle, projects, processes, selectedId, onSelect, onSaved, onError,
}: {
  bundle: ChronoBundle;
  projects: Array<{ id: string; name: string }>;
  processes: Array<{ id: string; name: string }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSaved: () => Promise<void>;
  onError: (error: unknown) => void;
}) {
  const study = bundle.study;
  const [title, setTitle] = useState(study.title);
  const [operation, setOperation] = useState(study.operationLabel);
  const [projectId, setProjectId] = useState(study.projectId ?? '');
  const [processId, setProcessId] = useState(study.processId ?? '');
  const [family, setFamily] = useState(study.productFamily ?? '');
  const [demand, setDemand] = useState(study.demandQuantity?.toString() ?? '');
  const [available, setAvailable] = useState(study.availableSeconds?.toString() ?? '');
  const [resourceKind, setResourceKind] = useState<ResourceKind>('operator');
  const [resourceName, setResourceName] = useState('');
  const [elementName, setElementName] = useState('');
  const [elementResource, setElementResource] = useState(bundle.resources[0]?.id ?? '');

  useEffect(() => {
    setTitle(study.title);
    setOperation(study.operationLabel);
    setProjectId(study.projectId ?? '');
    setProcessId(study.processId ?? '');
    setFamily(study.productFamily ?? '');
    setDemand(study.demandQuantity?.toString() ?? '');
    setAvailable(study.availableSeconds?.toString() ?? '');
  }, [study]);

  const previewTakt = taktSeconds(available === '' ? null : Number(available), demand === '' ? null : Number(demand));

  const saveStudy = async () => {
    try {
      await updateChronoStudy(study.id, {
        title,
        operationLabel: operation,
        projectId: projectId || null,
        processId: processId || null,
        productFamily: family || null,
        demandQuantity: demand === '' ? null : Number(demand),
        availableSeconds: available === '' ? null : Number(available),
      });
      await onSaved();
    } catch (error) { onError(error); }
  };

  const addResource = async () => {
    if (!resourceName.trim()) return;
    try {
      await addChronoResource(study.organizationId, study.id, resourceKind, resourceName, bundle.resources.length + 1);
      setResourceName('');
      await onSaved();
    } catch (error) { onError(error); }
  };

  const addElement = async () => {
    const resourceId = elementResource || bundle.resources[0]?.id;
    if (!elementName.trim() || !resourceId) return;
    try {
      await addChronoElement({
        organizationId: study.organizationId,
        studyId: study.id,
        scenarioId: bundle.currentScenarioId,
        resourceId,
        description: elementName,
        sortOrder: bundle.elements.reduce((max, element) => Math.max(max, element.sortOrder), 0) + 1,
      });
      setElementName('');
      await onSaved();
    } catch (error) { onError(error); }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2 border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-900 lg:grid-cols-8">
        <label className="text-[11px] lg:col-span-2">Título<input value={title} onChange={(event) => setTitle(event.target.value)} className={fieldClass} /></label>
        <label className="text-[11px]">Projeto
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)} className={fieldClass}>
            <option value="">Nenhum</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
        </label>
        <label className="text-[11px]">Processo
          <select value={processId} onChange={(event) => setProcessId(event.target.value)} className={fieldClass}>
            <option value="">Nenhum</option>
            {processes.map((process) => <option key={process.id} value={process.id}>{process.name}</option>)}
          </select>
        </label>
        <label className="text-[11px]">Operação<input value={operation} onChange={(event) => setOperation(event.target.value)} className={fieldClass} /></label>
        <label className="text-[11px]">Produto/família<input value={family} onChange={(event) => setFamily(event.target.value)} className={fieldClass} /></label>
        <label className="text-[11px]">Demanda<input value={demand} onChange={(event) => setDemand(event.target.value)} inputMode="decimal" className={fieldClass} /></label>
        <label className="text-[11px]">Disponível (s)<input value={available} onChange={(event) => setAvailable(event.target.value)} inputMode="decimal" className={fieldClass} /></label>
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span>Takt calculado {formatChronoNumber(previewTakt, 2)} s. Não é gravado.</span>
        <button type="button" onClick={saveStudy} className="rounded bg-slate-900 px-2 py-1 font-semibold text-white dark:bg-slate-100 dark:text-slate-900">Salvar estudo</button>
      </div>
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        <span className="font-semibold">Recursos</span>
        {bundle.resources.map((resource) => (
          <span key={resource.id} className="inline-flex items-center gap-1 border border-slate-300 px-1.5 py-0.5">
            {resource.kind === 'operator' ? 'Operador' : 'Máquina'} · {resource.name}
            <button type="button" aria-label={`Remover ${resource.name}`} onClick={async () => { try { await removeChronoResource(resource.id); await onSaved(); } catch (error) { onError(error); } }}>×</button>
          </span>
        ))}
        <select value={resourceKind} onChange={(event) => setResourceKind(event.target.value as ResourceKind)} className="border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-950">
          <option value="operator">Operador</option>
          <option value="machine">Máquina</option>
        </select>
        <input value={resourceName} onChange={(event) => setResourceName(event.target.value)} placeholder="Nome" className="w-28 border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-950" />
        <button type="button" onClick={addResource} className="border border-slate-400 px-1.5 py-0.5">Adicionar</button>
      </div>
      <ElementTable bundle={bundle} selectedId={selectedId} onSelect={onSelect} onSaved={onSaved} onError={onError} />
      <div className="flex gap-1 text-[11px]">
        <input value={elementName} onChange={(event) => setElementName(event.target.value)} placeholder="Novo elemento" className="min-w-48 flex-1 border border-slate-300 px-1.5 py-1 dark:border-slate-600 dark:bg-slate-950" />
        <select value={elementResource} onChange={(event) => setElementResource(event.target.value)} className="border border-slate-300 px-1 py-1 dark:border-slate-600 dark:bg-slate-950">
          {bundle.resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
        </select>
        <button type="button" onClick={addElement} className="border border-slate-400 px-2">Adicionar elemento</button>
      </div>
    </div>
  );
}

function ElementTable({
  bundle, selectedId, onSelect, onSaved, onError,
}: {
  bundle: ChronoBundle;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSaved: () => Promise<void>;
  onError: (error: unknown) => void;
}) {
  const resourceName = new Map(bundle.resources.map((resource) => [resource.id, resource.name]));
  const move = async (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= bundle.elements.length) return;
    const order = bundle.elements.map((element) => element.id);
    [order[index], order[next]] = [order[next], order[index]];
    try {
      await Promise.all(order.map((elementId, position) => updateChronoElement(elementId, { sortOrder: position + 1 })));
      await onSaved();
    } catch (error) { onError(error); }
  };
  return (
    <div className="overflow-auto border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full border-collapse text-left text-[11px]">
        <thead className="bg-slate-100 uppercase text-slate-600 dark:bg-slate-800">
          <tr>
            <th className="w-14 px-1 py-1">Seq</th>
            <th className="px-1 py-1">Elemento</th>
            <th className="px-1 py-1">Recurso</th>
            <th className="px-1 py-1">Natureza</th>
            <th className="px-1 py-1">AV/NAV/NAVN</th>
            <th className="px-1 py-1 text-right">Tempo (s)</th>
            <th className="w-8 px-1 py-1" />
          </tr>
        </thead>
        <tbody>
          {bundle.elements.map((element, index) => {
            const samples = bundle.observations.filter((item) => item.elementId === element.id).map((item) => item.seconds);
            const seconds = representativeSeconds(element.representativeMethod, element.representativeSeconds, observationStats(samples));
            return (
              <tr key={element.id} onClick={() => onSelect(element.id)} className={`border-t border-slate-200 ${selectedId === element.id ? 'bg-amber-50 dark:bg-amber-950' : ''}`}>
                <td className="px-1 py-0.5">
                  <button type="button" aria-label="Subir" onClick={(event) => { event.stopPropagation(); void move(index, -1); }}>↑</button>
                  <span className="px-1 tabular-nums">{index + 1}</span>
                  <button type="button" aria-label="Descer" onClick={(event) => { event.stopPropagation(); void move(index, 1); }}>↓</button>
                </td>
                <td className="px-1 py-0.5">
                  <input defaultValue={element.description} key={`${element.id}-${element.description}`} onBlur={(event) => { if (event.target.value !== element.description) void updateChronoElement(element.id, { description: event.target.value }).then(onSaved).catch(onError); }} onClick={(event) => event.stopPropagation()} className="w-full bg-transparent px-1 py-0.5" />
                </td>
                <td className="px-1 py-0.5">
                  <select value={element.resourceId} onClick={(event) => event.stopPropagation()} onChange={(event) => { void updateChronoElement(element.id, { resourceId: event.target.value }).then(onSaved).catch(onError); }} className="w-full bg-transparent">
                    {bundle.resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
                  </select>
                  <span className="sr-only">{resourceName.get(element.resourceId)}</span>
                </td>
                <td className="px-1 py-0.5">
                  <select value={element.nature} onClick={(event) => event.stopPropagation()} onChange={(event) => { void updateChronoElement(element.id, { nature: event.target.value as ElementNature }).then(onSaved).catch(onError); }} className="bg-transparent">
                    {Object.entries(NATURE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </td>
                <td className="px-1 py-0.5">
                  <select value={element.valueClass} onClick={(event) => event.stopPropagation()} onChange={(event) => { void updateChronoElement(element.id, { valueClass: event.target.value as ValueClass }).then(onSaved).catch(onError); }} className="bg-transparent">
                    {(['AV', 'NAVN', 'NAV'] as const).map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </td>
                <td className="px-1 py-0.5 text-right tabular-nums" title={METHOD_LABEL[element.representativeMethod]}>{formatChronoNumber(seconds, 3)}</td>
                <td className="px-1 py-0.5">
                  <button type="button" aria-label="Excluir elemento" onClick={(event) => { event.stopPropagation(); void removeChronoElement(element.id).then(onSaved).catch(onError); }}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TimingPane({
  bundle, selectedId, onSelect, onSaved, onError,
}: {
  bundle: ChronoBundle;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSaved: () => Promise<void>;
  onError: (error: unknown) => void;
}) {
  const element = bundle.elements.find((item) => item.id === selectedId) ?? bundle.elements[0] ?? null;
  const observations = bundle.observations.filter((item) => item.elementId === element?.id);
  const stats = observationStats(observations.map((item) => item.seconds));
  const active = element ? representativeSeconds(element.representativeMethod, element.representativeSeconds, stats) : null;
  const [manual, setManual] = useState(element?.representativeSeconds?.toString() ?? '');
  const [sample, setSample] = useState('');
  const [savingSample, setSavingSample] = useState(false);
  useEffect(() => setManual(element?.representativeSeconds?.toString() ?? ''), [element?.id, element?.representativeSeconds]);

  const saveMethod = async (method: RepresentativeMethod) => {
    if (!element) return;
    const seconds = method === 'manual'
      ? Number(manual === '' ? (stats.mean ?? 0) : manual)
      : null;
    try {
      await updateChronoElement(element.id, { representativeMethod: method, representativeSeconds: seconds });
      await onSaved();
    } catch (error) { onError(error); }
  };

  const saveManual = async () => {
    if (!element || manual === '') return;
    try {
      await updateChronoElement(element.id, { representativeMethod: 'manual', representativeSeconds: Number(manual) });
      await onSaved();
    } catch (error) { onError(error); }
  };

  const addSample = async () => {
    if (!element || sample === '' || savingSample) return;
    setSavingSample(true);
    try {
      await addChronoObservations({
        organizationId: bundle.study.organizationId,
        studyId: bundle.study.id,
        rows: [{ elementId: element.id, sequenceNo: observations.reduce((max, item) => Math.max(max, item.sequenceNo), 0) + 1, seconds: Number(sample) }],
      });
      setSample('');
      await onSaved();
    } catch (error) { onError(error); }
    finally { setSavingSample(false); }
  };

  return (
    <div className="grid gap-2 lg:grid-cols-[220px_1fr]">
      <div className="border border-slate-300 bg-white text-[11px] dark:border-slate-700 dark:bg-slate-900">
        {bundle.elements.map((item, index) => (
          <button key={item.id} type="button" onClick={() => onSelect(item.id)} className={`block w-full border-b border-slate-200 px-2 py-1 text-left ${item.id === element?.id ? 'bg-amber-50 dark:bg-amber-950' : ''}`}>
            {index + 1}. {item.description}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <CycleTimer bundle={bundle} onSaved={onSaved} onError={onError} onSelect={onSelect} />
        {element && (
          <div className="border border-slate-300 bg-white p-2 text-[11px] dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <strong>{element.description}</strong>
              <span>Método ativo: {METHOD_LABEL[element.representativeMethod]}</span>
              <span className="ml-auto tabular-nums">Representativo {formatChronoNumber(active, 3)} s</span>
            </div>
            <div className="mb-2 flex flex-wrap gap-1">
              {(Object.keys(METHOD_LABEL) as RepresentativeMethod[]).map((method) => (
                <button key={method} type="button" onClick={() => void saveMethod(method)} className={`border px-1.5 py-0.5 ${element.representativeMethod === method ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300'}`}>{METHOD_LABEL[method]}</button>
              ))}
              {element.representativeMethod === 'manual' && (
                <>
                  <input value={manual} onChange={(event) => setManual(event.target.value)} inputMode="decimal" className="w-24 border border-slate-300 px-1 dark:border-slate-600 dark:bg-slate-950" />
                  <button type="button" onClick={() => void saveManual()} className="border border-slate-400 px-1.5">Gravar manual</button>
                </>
              )}
            </div>
            <div className="mb-2 grid grid-cols-4 gap-x-3 gap-y-0.5 tabular-nums md:grid-cols-8">
              <span>N {stats.n}</span>
              <span>Média {formatChronoNumber(stats.mean, 3)}</span>
              <span>Mediana {formatChronoNumber(stats.median, 3)}</span>
              <span>Mín {formatChronoNumber(stats.min, 3)}</span>
              <span>Máx {formatChronoNumber(stats.max, 3)}</span>
              <span>R {formatChronoNumber(stats.range, 3)}</span>
              <span>s {formatChronoNumber(stats.stdev, 3)}</span>
              <span>CV {formatChronoNumber(stats.cv == null ? null : stats.cv * 100, 1)}%</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {observations.map((item) => (
                <span key={item.id} className="border border-slate-300 px-1 py-0.5 tabular-nums">
                  T{item.sequenceNo} {formatChronoNumber(item.seconds, 3)}
                  <button type="button" aria-label={`Excluir T${item.sequenceNo}`} className="ml-1" onClick={() => { void removeChronoObservation(item.id).then(onSaved).catch(onError); }}>×</button>
                </span>
              ))}
              <input value={sample} onChange={(event) => setSample(event.target.value)} placeholder="segundos" inputMode="decimal" className="w-20 border border-slate-300 px-1 dark:border-slate-600 dark:bg-slate-950" />
              <button type="button" disabled={savingSample} onClick={() => void addSample()} className="border border-slate-400 px-1.5 disabled:opacity-50">Registrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CycleTimer({
  bundle, onSaved, onError, onSelect,
}: {
  bundle: ChronoBundle;
  onSaved: () => Promise<void>;
  onError: (error: unknown) => void;
  onSelect: (id: string) => void;
}) {
  const anchor = useRef<number | null>(null);
  const accumulated = useRef(0);
  const [display, setDisplay] = useState(0);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [busy, setBusy] = useState(false);
  const elements = bundle.elements;

  useEffect(() => {
    if (!running || paused) return undefined;
    let frame = 0;
    const tick = () => {
      if (anchor.current != null) setDisplay((accumulated.current + performance.now() - anchor.current) / 1000);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, paused]);

  const readSeconds = () => {
    const elapsed = anchor.current == null ? accumulated.current : accumulated.current + performance.now() - anchor.current;
    return Math.round((elapsed / 1000) * 1000) / 1000;
  };
  const resetOpen = (keepRunning: boolean) => {
    accumulated.current = 0;
    anchor.current = keepRunning ? performance.now() : null;
    setPaused(false);
    setRunning(keepRunning);
    setDisplay(0);
  };
  const current = elements[index];

  const begin = () => {
    if (elements.length === 0) return;
    setIndex(0);
    setFinished(false);
    onSelect(elements[0].id);
    resetOpen(true);
  };
  const split = async (finish: boolean) => {
    if (!current || busy) return;
    const seconds = readSeconds();
    const sequence = bundle.observations.filter((item) => item.elementId === current.id).reduce((max, item) => Math.max(max, item.sequenceNo), 0) + 1;
    setBusy(true);
    try {
      await addChronoObservations({
        organizationId: bundle.study.organizationId,
        studyId: bundle.study.id,
        rows: [{ elementId: current.id, sequenceNo: sequence, seconds }],
      });
      const next = finish ? 0 : index + 1;
      setFinished(finish);
      setIndex(next);
      if (!finish && elements[next]) onSelect(elements[next].id);
      resetOpen(!finish);
      await onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
      <span className="w-20 font-semibold tabular-nums">{display.toFixed(3)} s</span>
      {!running && <button type="button" onClick={begin} className="bg-emerald-700 px-2 py-1 font-semibold text-white">{finished ? 'Próximo ciclo' : 'Iniciar ciclo'}</button>}
      {running && !paused && <button type="button" onClick={() => { accumulated.current += performance.now() - (anchor.current ?? performance.now()); anchor.current = null; setDisplay(accumulated.current / 1000); setPaused(true); }}>Pausar</button>}
      {running && paused && <button type="button" onClick={() => { anchor.current = performance.now(); setPaused(false); }}>Retomar</button>}
      {running && current && (
        <button type="button" disabled={busy} onClick={() => void split(index >= elements.length - 1)} className="bg-slate-900 px-2 py-1 font-semibold text-white disabled:opacity-50">
          {index >= elements.length - 1 ? 'Finalizar ciclo' : `Próximo: ${elements[index + 1]?.description ?? ''}`}
        </button>
      )}
      {running && current && <span className="text-[11px]">Medindo {index + 1}. {current.description}</span>}
      {running && <button type="button" onClick={() => { setFinished(false); resetOpen(false); }}>Cancelar intervalo</button>}
    </div>
  );
}

function AnalysisPane({ bundle }: { bundle: ChronoBundle }) {
  const takt = taktSeconds(bundle.study.availableSeconds, bundle.study.demandQuantity);
  const analysis = useMemo(() => quickAnalysis(bundle.elements.map((element) => ({
    id: element.id,
    resourceId: element.resourceId,
    valueClass: element.valueClass,
    nature: element.nature,
    method: element.representativeMethod,
    manualSeconds: element.representativeSeconds,
    samples: bundle.observations.filter((item) => item.elementId === element.id).map((item) => item.seconds),
  })), takt), [bundle, takt]);
  const names = new Map(bundle.elements.map((element) => [element.id, element.description]));
  const resources = new Map(bundle.resources.map((resource) => [resource.id, resource.name]));
  const rows: Array<[string, string]> = [
    ['Tempo total', `${formatChronoNumber(analysis.total, 3)} s`],
    ['AV', `${formatChronoNumber(analysis.av, 3)} s`],
    ['NAVN', `${formatChronoNumber(analysis.navn, 3)} s`],
    ['NAV', `${formatChronoNumber(analysis.nav, 3)} s`],
    ['% AV', analysis.avPercent == null ? '—' : `${formatChronoNumber(analysis.avPercent, 1)}%`],
    ['Manual', `${formatChronoNumber(analysis.manual, 3)} s`],
    ['Máquina', `${formatChronoNumber(analysis.machine, 3)} s`],
    ['Caminhada', `${formatChronoNumber(analysis.walking, 3)} s`],
    ['Espera', `${formatChronoNumber(analysis.waiting, 3)} s`],
    ['Takt', `${formatChronoNumber(analysis.takt, 2)} s`],
  ];
  return (
    <div className="grid gap-2 lg:grid-cols-[280px_1fr]">
      <table className="border-collapse border border-slate-300 bg-white text-[11px] dark:border-slate-700 dark:bg-slate-900">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-t border-slate-200">
              <th className="px-2 py-1 text-left font-medium">{label}</th>
              <td className="px-2 py-1 text-right tabular-nums">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[11px]">
        <p>Elementos acima do takt: {analysis.elementsAboveTakt.map((item) => names.get(item)).filter(Boolean).join(', ') || '—'}</p>
        <p>Recursos acima do takt: {analysis.resourcesAboveTakt.map((item) => resources.get(item)).filter(Boolean).join(', ') || '—'}</p>
        <p className="mt-1 text-slate-500">A carga do recurso é a soma dos tempos representativos dos seus elementos. Sobreposição ainda não entra.</p>
      </div>
    </div>
  );
}
